import axios from 'axios';

const ROOT_SELECTOR = '[data-article-feedback]';

type ArticleFeedbackPayload = {
  documentId: string;
  rating: 'positive' | 'negative';
};

export function initArticleFeedback(): void {
  document.querySelectorAll<HTMLElement>(ROOT_SELECTOR).forEach(initArticleFeedbackRoot);
}

function initArticleFeedbackRoot(feedbackSection: HTMLElement): void {
  const documentId = feedbackSection.dataset.documentId;
  const submittedMessage = feedbackSection.dataset.submittedMessage ?? 'Thanks for your feedback.';
  const title = feedbackSection.querySelector<HTMLElement>('.article-feedback__title');
  const status = feedbackSection.querySelector<HTMLElement>('[data-article-feedback-status]');
  const buttons = feedbackSection.querySelectorAll<HTMLButtonElement>('[data-article-feedback-rating]');

  if (!documentId || buttons.length === 0) {
    return;
  }

  let isSubmitting = false;

  const setLoadingState = (activeButton: HTMLButtonElement): void => {
    buttons.forEach((button) => {
      button.disabled = true;

      if (button === activeButton) {
        button.classList.add('article-feedback__button--loading');
        button.setAttribute('aria-busy', 'true');
      }
    });
  };

  const clearLoadingState = (): void => {
    buttons.forEach((button) => {
      button.classList.remove('article-feedback__button--loading');
      button.removeAttribute('aria-busy');
    });
  };

  const setSubmittedState = (message: string): void => {
    clearLoadingState();

    buttons.forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    });

    if (title) {
      title.textContent = message;
    }

    if (status) {
      status.hidden = true;
    }
  };

  const setErrorState = (message: string): void => {
    if (!status) {
      return;
    }

    status.textContent = message;
    status.hidden = false;
  };

  const submitFeedback = async (rating: 'positive' | 'negative', activeButton: HTMLButtonElement): Promise<void> => {
    if (isSubmitting) {
      return;
    }

    isSubmitting = true;
    setLoadingState(activeButton);

    if (status) {
      status.hidden = true;
    }

    try {
      const payload: ArticleFeedbackPayload = {
        documentId,
        rating,
      };

      await axios.post('/api/articles/feedback', payload);
      setSubmittedState(submittedMessage);
    } catch (error) {
      console.error('Failed to submit article feedback:', error);
      clearLoadingState();
      buttons.forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      });
      setErrorState('Something went wrong. Please try again.');
    } finally {
      isSubmitting = false;
    }
  };

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const rating = button.dataset.articleFeedbackRating as 'positive' | 'negative' | undefined;

      if (rating === 'positive' || rating === 'negative') {
        void submitFeedback(rating, button);
      }
    });
  });
}
